import {
  IconButton,
  Popover,
  PopoverTrigger,
  Tooltip,
  useBoolean,
} from '@chakra-ui/react';
import NotificationsOffSharpIcon from '@gamma/ui/icons/NotificationsOffSharpIcon';
import NotificationsSharpIcon from '@gamma/ui/icons/NotificationsSharpIcon';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { useMemo } from 'react';
import { i18n } from '@investigator/shared/localization';
import { useQueryAlertMetadataExcludedEntitiesPaginated } from '../../queries';
import { AlertEntity, Detection } from '@investigator/shared/generated/graphql';

import { SuppressEntityContent } from '../DetectionActions';
import { useEntitiesContext } from '@investigator/features/entities/context/EntitiesContext';
import { getEntityInstanceFromDetection } from '@investigator/shared/utils';

const { suppress, unSuppress, suppressed, unSuppressed } =
  i18n.pages.entityDetails.excludeEntity;

interface DetectionEntityPopoverProps {
  refetchMain?: boolean;
  detectionInfo: Detection;
  isEntityHovered: boolean;
  isEntitySuppressed: boolean;
  entityInfo?: Pick<AlertEntity, 'entity_name' | 'entity_type'>;
  setIsEntitySupressed: React.Dispatch<React.SetStateAction<boolean>>;
}

export const DetectionEntityPopover = ({
  entityInfo,
  refetchMain,
  detectionInfo,
  isEntityHovered,
  isEntitySuppressed,
}: DetectionEntityPopoverProps) => {
  const [isEditing, setIsEditing] = useBoolean();
  const { setSelectedEntity } = useEntitiesContext();

  const { alert_entity } = detectionInfo;
  const { entity_name, entity_type } = entityInfo || alert_entity;

  const handleOpenSuppress = () => {
    // Map detection data to EntityInstance and set in context
    const sourceEntity = entityInfo || alert_entity;
    if (sourceEntity && detectionInfo) {
      const entityInstance = {
        ...getEntityInstanceFromDetection(detectionInfo, sourceEntity),
        entityIsSuppressed: isEntitySuppressed,
      };
      setSelectedEntity(entityInstance);
    }
    setIsEditing.on();
  };

  const queryVariables = useMemo(() => {
    return {
      query:
        entity_type === 'DOMAIN'
          ? `{"query": {"bool": {"must": [{"match": {"content_id": "${
              detectionInfo?.alert_info?.content_id
            }"}}, {"term": {"entity_name.keyword": "${entity_name}"}}, {"term": {"entity_type": "${entity_type}"}}${
              detectionInfo?.tenant_info?.tenant_id ? ',' : ''
            }${
              detectionInfo?.tenant_info?.tenant_id
                ? `{"terms":{"tenant":["${detectionInfo?.tenant_info?.tenant_id}"]}}`
                : ''
            }]}}}`
          : `{"query": {"bool": {"must": [{"match": {"content_id": "${
              detectionInfo?.alert_info?.content_id
            }"}}, {"term": {"entity_type": "${entity_type}"}}, {"bool": {"should": [{"term": {"entity_ip": "${entity_name}"}}, {"range": {"entity_ip_range": {"from": "${entity_name}", "to": "${entity_name}"}}}]}}${
              detectionInfo?.tenant_info?.tenant_id ? ',' : ''
            }${
              detectionInfo?.tenant_info?.tenant_id
                ? `{"terms":{"tenant":["${detectionInfo?.tenant_info?.tenant_id}"]}}`
                : ''
            }]}}}`,
      offset: 0,
      size: 10,
      sort: [
        {
          sort_by: 'entity_name.keyword',
          sort_dir: 'desc',
        },
      ],
    };
  }, [detectionInfo, entity_name, entity_type]);

  const { loading: _excludedEntitiesLoading, error: excludedEntitiesError } =
    useQueryAlertMetadataExcludedEntitiesPaginated({
      skip:
        !entity_name ||
        !entity_type ||
        !detectionInfo?.tenant_info?.tenant_id ||
        !detectionInfo?.alert_info?.content_id,
      variables: queryVariables,
    });

  if (excludedEntitiesError) {
    return (
      <GraphQLReqStatus
        loading={false}
        isBackground={true}
        error={excludedEntitiesError}
      ></GraphQLReqStatus>
    );
  }

  return (
    <Popover
      closeOnEsc={true}
      closeOnBlur={true}
      offset={[-145, 10]}
      isOpen={isEditing}
      onOpen={handleOpenSuppress}
      onClose={setIsEditing.off}
    >
      <>
        {isEditing || isEntityHovered ? (
          <PopoverTrigger>
            <span>
              <Tooltip
                textStyle="body-md"
                label={isEntitySuppressed ? unSuppress : suppress}
              >
                <IconButton
                  w="24px"
                  h="24px"
                  size="box-md"
                  variant="solid"
                  colorScheme="gray"
                  data-testid="suppress-entity-popover-content-trigger"
                  aria-label={isEntitySuppressed ? unSuppressed : suppressed}
                >
                  {isEntitySuppressed ? (
                    <NotificationsSharpIcon size="sm" />
                  ) : (
                    <NotificationsOffSharpIcon size="sm" />
                  )}
                </IconButton>
              </Tooltip>
            </span>
          </PopoverTrigger>
        ) : null}
        <SuppressEntityContent
          refetchMain={refetchMain}
          onClose={setIsEditing.off}
        />
      </>
    </Popover>
  );
};
